/**
 * Popup DOM Operations Module
 * Handles creation, updating and manipulation of popup DOM elements
 */
(function () {
  const _showEle = (ele, animateCb = () => {}) => {
    if (!ele.classList.contains("erj-popup-transition")) {
      ele.classList.add("erj-popup-transition");
    }

    ele.style.display = "flex";

    animateCb && animateCb(ele);

    // Set opacity after a small delay to ensure transition works
    requestAnimationFrame(() => {
      ele.style.opacity = "1";
      ele.style.display = "flex";
    });
  };

  const _hideEle = (ele, animateCb = () => {}) => {
    if (!ele.classList.contains("erj-popup-transition")) {
      ele.classList.add("erj-popup-transition");
    }

    ele.style.opacity = "0";

    animateCb && animateCb(ele);

    // NOTE - `transitionend` event is not reliable, so we use a timeout to ensure the element is hidden after the transition
    setTimeout(
      () => {
        if (ele.style.opacity === "0") {
          ele.style.display = "none";
        }
      },
      // NOTE - should match CSS variable --erj-popup-transition-duration
      300
    );
  };

  const _getPopupEle = (popupId) => {
    return document.querySelector(`[data-erj-popup-id="${popupId}"]`);
  };

  // Set popup interaction disabled state
  const _setPopupInteractionDisabled = (popup, isDisabled) => {
    const container = popup.querySelector(".erj-popup-container");

    // Check current state
    const currentInteraction = popup.getAttribute("data-erj-popup-interaction");
    const newInteraction = isDisabled ? "disabled" : "enabled";

    // Only perform operations when state needs to change
    if (currentInteraction !== newInteraction) {
      popup.setAttribute("data-erj-popup-interaction", newInteraction);
      if (container) {
        container.setAttribute("data-erj-popup-interaction", newInteraction);
      }
    }
  };

  const _imgAreaCtrl = (popupId) => {
    const popup = _getPopupEle(popupId);
    const container = popup.querySelector(".erj-popup-container");
    const currentStep = container.getAttribute("data-erj-popup-current-step");
    const closeButton = container.querySelector(".erj-popup-close");
    const imagesContainer = popup.querySelector(".erj-popup-image");

    if (!imagesContainer) return false;

    const allImages = imagesContainer.querySelectorAll(
      "img[data-erj-popup-step]"
    );

    // Confirm current step image
    let currentStepImage = null;

    if (currentStep === "2") {
      const step2 = popup.querySelector(
        ".erj-popup-content [data-erj-popup-step='2']"
      );
      const resultType = step2
        ? step2.getAttribute("data-erj-popup-current-result")
        : null;

      if (resultType) {
        currentStepImage = imagesContainer.querySelector(
          `img[data-erj-popup-step="2"][data-erj-popup-result="${resultType}"]`
        );
      }
    } else {
      currentStepImage = imagesContainer.querySelector(
        `img[data-erj-popup-step="${currentStep}"]`
      );
    }

    const isAreaAlreadyShown =
      getComputedStyle(imagesContainer).display !== "none";

    if (currentStepImage) {
      closeButton &&
        closeButton.setAttribute("data-erj-popup-image-step", "true");

      if (isAreaAlreadyShown) {
        // Hide all images except current step image
        [...allImages].forEach((img) => {
          if (img !== currentStepImage) {
            _hideEle(img, (ele) => {
              ele.style.width = "0";
            });
          } else {
            _showEle(img);
          }
        });
      } else {
        _showEle(imagesContainer, (ele) => {
          ele.style.flexBasis = "50%";
        });
        _showEle(currentStepImage);
      }
    } else {
      closeButton && closeButton.removeAttribute("data-erj-popup-image-step");
      // Hide all
      _hideEle(imagesContainer, (ele) => {
        ele.style.flexBasis = "0";
      });
      [...allImages].forEach((img) => {
        _hideEle(img);
      });
    }

    return true;
  };

  // DOM Operations API
  const PopupDOM = {
    /**
     * Show popup
     * @param {String} popupId Popup ID
     */
    showPopup(popupId, isManually = false) {
      const popup = _getPopupEle(popupId);
      if (!popup) {
        return false;
      }

      // Reset state
      this.resetPopupState(popup);

      // Show popup overlay
      popup.setAttribute("data-erj-popup-state", "visible");

      // Disable interaction during animation
      _setPopupInteractionDisabled(popup, true);

      // Clear state after animation completes
      popup.addEventListener(
        "transitionend",
        () => {
          _setPopupInteractionDisabled(popup, false);
        },
        { once: true }
      );

      // Set initial step
      this.setStep(popupId, 1);

      window._erj_?.trackSubscribePopupOpen?.("newsletter",isManually);

      return true;
    },

    /**
     * Hide popup
     * @param {String} popupId Popup ID
     */
    hidePopup(popupId) {
      const popup = _getPopupEle(popupId);
      if (!popup) {
        return false;
      }

      // Disable interaction during animation
      _setPopupInteractionDisabled(popup, true);

      // Hide popup
      popup.setAttribute("data-erj-popup-state", "hidden");

      // Wait for transition to complete before resetting state
      popup.addEventListener(
        "transitionend",
        () => {
          this.resetPopupState(popup);
          _setPopupInteractionDisabled(popup, false);
        },
        { once: true }
      );

      return true;
    },

    /**
     * Set step
     * @param {String} popupId Popup ID
     * @param {Number} step Step number
     * @param {String} [result] Result type for step 2: 'success' or 'subscribed'
     */
    setStep(popupId, step, result) {
      const popup = _getPopupEle(popupId);
      if (!popup) {
        return false;
      }

      // Disable interaction during animation
      _setPopupInteractionDisabled(popup, true);

      // Get container and content
      const container = popup.querySelector(".erj-popup-container");
      const content = container.querySelector(".erj-popup-content");

      // Set current step
      // TODO - update to popup store
      container.setAttribute("data-erj-popup-current-step", step);

      // Hide all step content
      const allSteps = content.querySelectorAll("[data-erj-popup-step]");
      allSteps.forEach((stepEl) => {
        _hideEle(stepEl);
      });

      // Show current step contents
      [...allSteps].forEach((stepEl) => {
        if (stepEl.getAttribute("data-erj-popup-step") === `${step}`) {
          _showEle(stepEl);
        }
      });

      switch (step) {
        case 1:
          break;
        case 2: {
          // TODO - consider types of popup
          const step2 = [...allSteps].find(
            (stepEl) => stepEl.getAttribute("data-erj-popup-step") === `${step}`
          );

          if (!step2) {
            break;
          }

          if (result) {
            // Hide all result states first
            const resultElements = step2.querySelectorAll(".erj-popup-result");

            resultElements.forEach((el) => {
              _hideEle(el);
            });

            step2.setAttribute("data-erj-popup-current-result", result);

            // Show corresponding result state
            const activeResult = step2.querySelector(
              `[data-erj-popup-result="${result}"]`
            );

            if (activeResult) {
              _showEle(activeResult);
            }
          } else {
            step2.removeAttribute("data-erj-popup-current-result");
          }
          break;
        }
      }

      // Update image area
      _imgAreaCtrl(popupId);

      // Add timeout safeguard to ensure state is cleared even if transitionend event doesn't fire
      const animationTimeout = setTimeout(() => {
        _setPopupInteractionDisabled(popup, false);
      }, 500);

      // Clear state after animation completes
      container.addEventListener(
        "transitionend",
        () => {
          // Clear timeout to prevent duplicate execution
          clearTimeout(animationTimeout);
          _setPopupInteractionDisabled(popup, false);
        },
        { once: true }
      );

      return true;
    },

    /**
     * Set loading state
     * @param {String} popupId Popup ID
     * @param {Boolean} isLoading Whether loading
     */
    setLoading(popupId, isLoading) {
      const popup = _getPopupEle(popupId);
      if (!popup) return false;

      const container = popup.querySelector(".erj-popup-container");
      container.setAttribute(
        "data-erj-popup-loading",
        isLoading ? "true" : "false"
      );

      return true;
    },

    /**
     * Reset popup state
     * @private
     * @param {Element} popup Popup element
     */
    resetPopupState(popup) {
      const container = popup.querySelector(".erj-popup-container");

      // remove all transition class
      const activeElements = popup.querySelectorAll(".erj-popup-transition");
      activeElements.forEach((el) => {
        el.classList.remove("erj-popup-transition");
        el.style.opacity = "0";
        el.style.display = "none";
      });

      // Reset all states
      container.removeAttribute("data-erj-popup-result");
      container.removeAttribute("data-erj-popup-loading");
      container.removeAttribute("data-erj-popup-current-step");

      // Set interaction state to enabled
      popup.setAttribute("data-erj-popup-interaction", "enabled");
      if (container) {
        container.setAttribute("data-erj-popup-interaction", "enabled");
      }
    },
  };

  // Export to global object
  window._erj_ = window._erj_ || {};
  window._erj_.PopupDOM = PopupDOM;
})();
